import { useEffect, useRef } from 'react';
import type { Manifest, Pin, Memory } from '@/types';

/**
 * Detect the moment a pin or memory transitions from locked → unlocked while
 * the app is open. Triggered by:
 *   • the client clock crossing an unlock_at (caught by useManifest re-render)
 *   • a server manifest bump (after a geofence/condition unlock)
 *
 * We diff the latest manifest against the previous render's snapshot.
 * Pin-level transitions emit an UnlockEvent; memory-level transitions inside
 * an already-unlocked pin (dated letters) emit MemoryUnlockEvents.
 */

export interface PinUnlockEvent {
  kind: 'pin';
  pin: Pin;
  at: number;
}
export interface MemoryUnlockEvent {
  kind: 'memory';
  memory: Memory;
  pin: Pin;
  at: number;
}
export type UnlockEvent = PinUnlockEvent | MemoryUnlockEvent;

type Subscriber = (e: UnlockEvent) => void;

const subscribers = new Set<Subscriber>();

export function subscribeUnlocks(cb: Subscriber): () => void {
  subscribers.add(cb);
  return () => { subscribers.delete(cb); };
}

function emit(e: UnlockEvent) {
  for (const s of subscribers) s(e);
}

export function useUnlockDetection(manifest: Manifest | undefined): void {
  // First render: snapshot without firing. Subsequent renders: diff and emit
  // for any item whose state moved from locked → unlocked.
  const seeded = useRef(false);
  const prevPinState = useRef<Map<string, 'locked' | 'unlocked'>>(new Map());
  const prevMemState = useRef<Map<string, boolean>>(new Map()); // memory.id -> locked

  useEffect(() => {
    if (!manifest) return;

    const nextPinState = new Map<string, 'locked' | 'unlocked'>();
    const nextMemState = new Map<string, boolean>();

    for (const pin of manifest.pins) {
      nextPinState.set(pin.id, pin.current_state);
      if (seeded.current) {
        const was = prevPinState.current.get(pin.id);
        if (was === 'locked' && pin.current_state === 'unlocked') {
          emit({ kind: 'pin', pin, at: Date.now() });
        }
      }
      for (const m of pin.memories) {
        nextMemState.set(m.id, m.locked);
        if (seeded.current) {
          const wasLocked = prevMemState.current.get(m.id);
          // Only celebrate memory-level unlocks if the parent pin was already
          // unlocked, to avoid double-celebration when a pin opens.
          if (wasLocked === true && m.locked === false && pin.current_state === 'unlocked') {
            emit({ kind: 'memory', memory: m, pin, at: Date.now() });
          }
        }
      }
    }

    prevPinState.current = nextPinState;
    prevMemState.current = nextMemState;
    seeded.current = true;
  }, [manifest]);
}
